import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  ALL_CATEGORIES,
  ANON_ID_KEY,
  OPTIONAL_CATEGORIES,
  STORAGE_KEY,
  type ConsentAction,
  type ConsentContextValue,
  type ConsentState,
  type CookieCategory,
} from "./types";

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

function getOrCreateAnonId(): string {
  if (typeof window === "undefined") return "";
  try {
    const existing = localStorage.getItem(ANON_ID_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(ANON_ID_KEY, id);
    return id;
  } catch {
    return "anon-" + Math.random().toString(36).slice(2);
  }
}

function loadStored(): ConsentState | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as ConsentState;
    if (!parsed?.categories || !parsed.policyVersion) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

function persist(state: ConsentState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function emptyCategories(): Record<CookieCategory, boolean> {
  return {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
    third_party: false,
  };
}

interface ConsentProviderProps {
  children: ReactNode;
  /** Current published cookie-policy version. Defaults to a constant fallback. */
  currentPolicyVersion?: string;
}

const FALLBACK_VERSION = "2026.04.29";

export function ConsentProvider({
  children,
  currentPolicyVersion,
}: ConsentProviderProps) {
  const [state, setState] = useState<ConsentState | undefined>(() => loadStored());
  const [policyVersion, setPolicyVersion] = useState<string>(
    currentPolicyVersion ?? FALLBACK_VERSION,
  );
  const [managerOpen, setManagerOpen] = useState(false);

  // Fetch the latest published cookie-policy version once at mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("privacy_policy_versions")
        .select("version, forces_reconsent, effective_at")
        .eq("policy_type", "cookies")
        .order("effective_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled || !data) return;
      setPolicyVersion(data.version);
      // Force re-consent when admin published a newer mandatory version.
      if (data.forces_reconsent && state && state.policyVersion !== data.version) {
        setState(undefined);
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          /* ignore */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // intentionally only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recordDecision = useCallback(
    async (next: ConsentState, action: ConsentAction) => {
      const accepted = ALL_CATEGORIES.filter((c) => next.categories[c]);
      const rejected = ALL_CATEGORIES.filter((c) => !next.categories[c]);
      const { data: userResult } = await supabase.auth.getUser();
      const userId = userResult?.user?.id ?? null;

      try {
        await supabase.from("consent_records").insert({
          user_id: userId,
          anonymous_id: userId ? null : next.anonymousId,
          policy_version: next.policyVersion,
          categories_accepted: accepted,
          categories_rejected: rejected,
          user_agent:
            typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
          origin: "web",
          action,
        });
      } catch (err) {
        // Non-blocking: persistence shouldn't break UX.
        // eslint-disable-next-line no-console
        console.warn("[consent] failed to record", err);
      }
    },
    [],
  );

  const apply = useCallback(
    async (
      categories: Record<CookieCategory, boolean>,
      action: ConsentAction,
    ) => {
      const next: ConsentState = {
        categories: { ...categories, necessary: true },
        policyVersion,
        decidedAt: new Date().toISOString(),
        action,
        anonymousId: state?.anonymousId ?? getOrCreateAnonId(),
      };
      setState(next);
      persist(next);
      setManagerOpen(false);
      void recordDecision(next, action);
    },
    [policyVersion, recordDecision, state?.anonymousId],
  );

  const acceptAll = useCallback(async () => {
    const all = ALL_CATEGORIES.reduce(
      (acc, c) => ({ ...acc, [c]: true }),
      {} as Record<CookieCategory, boolean>,
    );
    await apply(all, "accept_all");
  }, [apply]);

  const rejectOptional = useCallback(async () => {
    await apply(emptyCategories(), "reject_optional");
  }, [apply]);

  const saveCustom = useCallback(
    async (categories: Partial<Record<CookieCategory, boolean>>) => {
      const merged: Record<CookieCategory, boolean> = {
        ...emptyCategories(),
        ...categories,
        necessary: true,
      };
      await apply(merged, "custom");
    },
    [apply],
  );

  const revoke = useCallback(async () => {
    await apply(emptyCategories(), "revoke");
  }, [apply]);

  const canLoad = useCallback(
    (category: CookieCategory) => {
      if (category === "necessary") return true;
      return Boolean(state?.categories[category]);
    },
    [state],
  );

  const needsDecision =
    !state || (state.policyVersion !== policyVersion && OPTIONAL_CATEGORIES.length > 0);

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      needsDecision,
      managerOpen,
      openManager: () => setManagerOpen(true),
      closeManager: () => setManagerOpen(false),
      acceptAll,
      rejectOptional,
      saveCustom,
      revoke,
      canLoad,
    }),
    [state, needsDecision, managerOpen, acceptAll, rejectOptional, saveCustom, revoke, canLoad],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used inside <ConsentProvider>");
  }
  return ctx;
}
